import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Heart, Trash2, X, ExternalLink, ShoppingBag } from 'lucide-react';
import Toast from '../components/Toast';

const WishlistScreen = () => {
  const { wishlist, removeFromWishlist, setActiveTab, setCurrentScreen } = useApp();
  const [selectedItem, setSelectedItem] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const handleRemove = (itemId) => {
    removeFromWishlist(itemId);
    setToast({ visible: true, message: 'Removed from wishlist', type: 'success' });
    if (selectedItem && selectedItem.id === itemId) {
      setSelectedItem(null);
    }
  };

  const handleStartScan = () => {
    setActiveTab('scan');
    setCurrentScreen('scan');
  };

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen bg-white pb-24 flex flex-col">
        {/* Header */}
        <div className="px-6 pt-10 pb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2">Saved</p>
          <h1 className="font-sans text-2xl font-light text-black">
            Wishlist
          </h1>
          <p className="text-black/55 text-sm font-light mt-1">
            Your saved garments
          </p>
        </div>

        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-20 h-20 bg-white/[0.04] rounded-3xl flex items-center justify-center mb-5">
            <Heart size={28} strokeWidth={1.5} className="text-gray-400" />
          </div>
          <h2 className="font-serif text-xl font-light text-gray-900 text-center mb-2">
            Your Wishlist is Empty
          </h2>
          <p className="text-gray-400 text-center text-sm font-light max-w-[280px] leading-relaxed">
            Save garments you love and return when you're ready to decide. Your curated collection awaits.
          </p>
          <button
            onClick={handleStartScan}
            className="mt-6 px-8 py-3 bg-white text-gray-900 rounded-2xl text-sm font-light tracking-wider
              flex items-center gap-3 hover:bg-gray-50 transition-all duration-500 shadow-lg shadow-[#000000]/10"
          >
            <ShoppingBag size={16} strokeWidth={1.5} />
            <span>Start Exploring</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-10 pb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2">Saved</p>
        <h1 className="font-sans text-2xl font-light text-black">
          Wishlist
        </h1>
        <p className="text-black/55 text-sm font-light mt-1">
          {wishlist.length} saved {wishlist.length === 1 ? 'item' : 'items'}
        </p>
      </div>

      {/* Wishlist Items */}
      <div className="px-6 space-y-3">
        {wishlist.map((item) => (
          <div
            key={item.id}
            className="premium-card rounded-2xl luxury-border overflow-hidden
              transform hover:scale-[1.005] transition-all duration-500"
          >
            <div className="flex">
              <img
                src={item.imageUrl}
                alt={item.name}
                className="w-24 h-24 object-cover cursor-pointer"
                onClick={() => setSelectedItem(item)}
              />
              <div className="p-3 flex-1 min-w-0">
                <p className="text-[10px] text-gray-600 font-medium uppercase tracking-[0.15em]">
                  {item.brand}
                </p>
                <h3 
                  className="text-sm font-medium text-gray-900 mt-0.5 truncate cursor-pointer
                    hover:text-gray-900 transition-colors"
                  onClick={() => setSelectedItem(item)}
                >
                  {item.name}
                </h3>
                <p className="text-black font-sans font-light text-sm mt-1">
                  {item.tags?.slice(0, 1).join(', ')}
                </p>
                <div className="flex gap-1 mt-1.5">
                  {item.tags.slice(0, 2).map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-gray-50/[0.03] text-gray-400 text-[10px] rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 flex flex-col justify-between">
                <button
                  onClick={() => handleRemove(item.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg
                    text-gray-200 hover:text-[#B85C3B] hover:bg-[#B85C3B]/10 transition-all duration-300"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
                <a
                  href={item.buyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-lg
                    bg-white text-gray-900 hover:bg-gray-50 transition-all duration-300"
                >
                  <ExternalLink size={12} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scan More CTA */}
      <div className="px-6 mt-6">
        <button
          onClick={handleStartScan}
          className="w-full py-3 border border-dashed border-[#000000]/20 rounded-2xl
            text-gray-500 text-xs font-light tracking-wider flex items-center justify-center gap-2
            hover:border-[#000000]/30 hover:bg-white/[0.02] transition-all duration-500"
        >
          <ShoppingBag size={16} strokeWidth={1.5} />
          <span>Discover More Alternatives</span>
        </button>
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-white/30 backdrop-blur-sm z-50 flex items-end justify-center"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-gray-50 rounded-t-3xl w-full max-w-md max-h-[80vh] overflow-y-auto animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-50/80 backdrop-blur-xl px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-serif text-lg font-light text-gray-900">
                Wishlist Item
              </h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50/[0.03]
                  text-gray-400 hover:bg-gray-50/[0.06] transition-all"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <img
                src={selectedItem.imageUrl}
                alt={selectedItem.name}
                className="w-full aspect-[4/3] object-cover rounded-2xl mb-5"
              />
              
              <p className="text-[10px] text-gray-600 font-medium uppercase tracking-[0.15em]">
                {selectedItem.brand}
              </p>
              <h4 className="font-serif text-2xl font-light text-gray-900 mt-1">
                {selectedItem.name}
              </h4>

              <div className="flex flex-wrap gap-2 mt-4">
                {selectedItem.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-gray-50/[0.03] text-white/80 text-[11px] font-light rounded-lg
                      tracking-wide"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-gray-500 text-sm font-light leading-relaxed mt-5">
                {selectedItem.reason}
              </p>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    handleRemove(selectedItem.id);
                  }}
                  className="flex-1 py-3 bg-gray-50/[0.03] text-[#B85C3B] rounded-xl text-xs font-light tracking-wider
                    flex items-center justify-center gap-2 hover:bg-[#B85C3B]/10 transition-all duration-300"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                  Remove
                </button>
                <a
                  href={selectedItem.buyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-white text-gray-900 rounded-xl text-xs font-light tracking-wider
                    flex items-center justify-center gap-2 hover:bg-gray-50 transition-all duration-300"
                >
                  <ExternalLink size={14} strokeWidth={1.5} />
                  Buy Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast({ ...toast, visible: false })}
      />
    </div>
  );
};

export default WishlistScreen;
